import React, { Children, createContext, useContext, useEffect, useMemo, useState } from 'react';

const RouterContext = createContext(null);

export function BrowserRouter({ children }) {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const value = useMemo(() => ({
    pathname,
    navigate(to) {
      const next = new URL(to, window.location.origin);
      window.history.pushState({}, '', `${next.pathname}${next.search}${next.hash}`);
      setPathname(next.pathname);
    }
  }), [pathname]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useLocation() {
  const router = useContext(RouterContext);
  return { pathname: router.pathname };
}

export function Link({ to, onClick, children, ...props }) {
  const router = useContext(RouterContext);
  return <a {...props} href={to} onClick={(event) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (/^(https?:|mailto:|tel:)/.test(to)) return;
    event.preventDefault();
    router.navigate(to);
  }}>{children}</a>;
}

export function NavLink({ to, end = false, className = '', children, ...props }) {
  const { pathname } = useContext(RouterContext);
  const active = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
  const resolvedClass = typeof className === 'function' ? className({ isActive: active }) : `${className}${active ? ' active' : ''}`.trim();
  return <Link {...props} to={to} className={resolvedClass}>{children}</Link>;
}

export function Route() {
  return null;
}

export function Routes({ children }) {
  const { pathname } = useContext(RouterContext);
  const routes = Children.toArray(children);
  const selected = routes.find((route) => route.props.path === pathname) || routes.find((route) => route.props.path === '*');
  return selected?.props.element || null;
}
